import React from 'react';
import {
  Box,
  HStack,
  Spinner,
  Text,
  Button,
  ButtonText,
  ButtonIcon,
  Tooltip,
  TooltipContent,
  TooltipText,
} from '@gluestack-ui/themed';
import { BulkActionsOverlayProps } from '../types';

/**
 * BulkActionsOverlay - A floating toolbar that appears when rows are selected in the DataTable.
 *
 * It provides:
 * - A count of currently selected items.
 * - A "Clear selection" button to reset the selection state.
 * - A dynamic list of bulk actions (e.g., Delete, Export) based on the current object type.
 * - Visual feedback (spinner) when a bulk action is being executed.
 *
 * It renders using absolute positioning to overlay the standard DataTableFunctionBar.
 *
 * @param {BulkActionsOverlayProps} props - Configuration for selected rows, available actions, and callbacks.
 * @returns {React.ReactElement | null} The overlay toolbar or null if no rows are selected.
 */
const BulkActionsOverlay: React.FC<BulkActionsOverlayProps> = ({
  selectedRows,
  bulkActions,
  objectName,
  onAction,
  onClearSelection,
  isActionRunning,
}) => {
  if (selectedRows.length === 0) return null;

  return (
    <HStack
      position="absolute"
      top={-6}
      left={-8}
      right={-8}
      bottom={-6}
      bg="$bgSubtle"
      zIndex={10}
      px="$5"
      alignItems="center"
      justifyContent="space-between"
      borderRadius="$lg"
      borderWidth={1}
      borderColor="$borderLight"
    >
      <HStack alignItems="center" space="lg">
        <HStack alignItems="center" space="sm">
          {isActionRunning ? (
            <Spinner size="small" color="$brandBase" />
          ) : (
            <Box bg="$brandBase" borderRadius="$full" w={24} h={24} alignItems="center" justifyContent="center">
              <Text size="xs" fontWeight="$bold" color="$textInverted">
                {selectedRows.length}
              </Text>
            </Box>
          )}
          <Text size="sm" color="$textBody">
            {isActionRunning ? 'Executing bulk action...' : `${selectedRows.length === 1 ? 'item' : 'items'} selected`}
          </Text>
        </HStack>
        <Button size="xs" variant="link" onPress={onClearSelection} isDisabled={isActionRunning}>
          <ButtonText size="xs" color="$textSubtle">
            Clear selection
          </ButtonText>
        </Button>
      </HStack>

      <HStack space="sm">
        {bulkActions
          ?.filter(([, action]) => (action.availCondition ? action.availCondition(selectedRows, objectName) : true))
          .map(([actionCode, action]) => {
            const isDelete = actionCode === 'delete' || actionCode === 'bulkDelete';
            return (
              <Tooltip
                key={actionCode}
                placement="top"
                trigger={(triggerProps) => (
                  <Button
                    {...triggerProps}
                    variant="solid"
                    size="xs"
                    action={isDelete ? 'negative' : 'secondary'}
                    borderRadius="$md"
                    onPress={() => onAction?.(actionCode, selectedRows)}
                    testID={`bulk-action-button-${actionCode}`}
                    isDisabled={isActionRunning}
                    px="$3"
                    h={32}
                  >
                    {!!action.icon && <ButtonIcon as={action.icon} size="xs" mr="$1.5" />}
                    <ButtonText>
                      {action.label}
                    </ButtonText>
                  </Button>
                )}
              >
                <TooltipContent>
                  <TooltipText>{action.label || actionCode}</TooltipText>
                </TooltipContent>
              </Tooltip>
            );
          })}
      </HStack>
    </HStack>
  );
};

export default BulkActionsOverlay;
